//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useEffect, useRef } from 'react';
import {
  Box, CircularProgress, ClickAwayListener, Grow, MenuItem, MenuList, Paper,
  Popper, Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { focusFirstMenuItem, handleMenuKeyDown } from './menuKeyboard';
import { HEADER_TOUR_ANCHORS } from './headerTourAnchors';
import { isWithinTourTooltip } from '@/components/Help/tours/tourDom';
import { useHelp } from '@/hooks/useHelp';
import { demoData } from '@/utils/DemoDatasets';

interface DemoDataMenuProps {
  open: boolean;
  // Whatever activated the tool: the taskbar pill or the Tools row.
  anchorEl: HTMLElement | null;
  // Both keyed by dataset name, the shape useDemoDataLoader reports.
  isLoading: Record<string, boolean>;
  isLoaded: Record<string, boolean>;
  onLoad: (datasetName: string) => void;
  onClose: () => void;
}

/**
 * The desktop demo-dataset menu, anchored to whichever control opened it.
 * The mobile drawer draws the same datasets as rows instead; see
 * `DemoDrawerSection`.
 */
const DemoDataMenu = ({
  open, anchorEl, isLoading, isLoaded, onLoad, onClose
}: DemoDataMenuProps) => {
  const theme = useTheme();
  const menuRef = useRef<HTMLUListElement>(null);
  const { tourState: { activeTour } } = useHelp();

  // A close never aims at a node that has left the document: the demo tool
  // is a pinned taskbar item, and a refit between open and close would
  // otherwise leave focus falling to the body. Matches handleMenuKeyDown.
  const closeAndRefocus = () => {
    onClose();
    if (anchorEl?.isConnected) anchorEl.focus();
  };

  const handleChoose = (datasetName: string) => {
    onLoad(datasetName);
    // A menu that has done its job closes. Waiting for a click away is not
    // enough: a running tour suppresses that for clicks on its own tooltip,
    // so the menu would stay open over the header for the rest of the tour.
    closeAndRefocus();
  };

  // The click-away the tour's tooltip is exempt from falls due when the
  // tour ends: a leave button there ends the tour without any click the
  // menu can see, and once the tooltip has unmounted nothing else would
  // close a menu left standing over the header. Only a tour this open menu
  // saw running counts, so a tour ending before it opened is not its cue.
  const tourWasRunning = useRef(false);
  useEffect(() => {
    if (tourWasRunning.current && activeTour === null && open)
      closeAndRefocus();
    tourWasRunning.current = activeTour !== null;
  });

  return (
    <Popper
      id="menu-appbar"
      open={open}
      anchorEl={anchorEl}
      transition
      placement="bottom-end"
      style={{ zIndex: theme.zIndex.modal }}
      modifiers={[
        // A little space between the anchor -- the taskbar pill or the
        // Tools row -- and the menu.
        { name: 'offset', options: { offset: [0, 5] } }
      ]}
    >
      {({ TransitionProps }) => (
        <Grow {...TransitionProps}
          style={{ transformOrigin: 'top right' }}
          onEntered={() => focusFirstMenuItem(menuRef)}
        >
          <Paper elevation={6}>
            <ClickAwayListener onClickAway={(event) => {
                // The tour's tooltip is a portal beside the app, so a click
                // on it reads as a click away from the menu the tour is
                // pointing at.
                if (isWithinTourTooltip(event.target)) return;
                onClose();
              }}>
              <MenuList
                ref={menuRef}
                data-joyride-id={HEADER_TOUR_ANCHORS.demoDataMenu}
                role="menu"
                tabIndex={-1}
                disablePadding
                onKeyDown={(e) => handleMenuKeyDown(e, onClose, anchorEl)}
              >
                {Object.entries(demoData).map(
                  ([datasetName, { label, icon, description }]) => (
                  <Tooltip describeChild
                    key={datasetName}
                    title={description}
                    placement="left"
                    arrow
                  >
                    <span>
                      <MenuItem
                        onClick={() => handleChoose(datasetName)}
                        role="menuitem"
                        disabled={
                          isLoading[datasetName] || isLoaded[datasetName]}
                        aria-busy={isLoading[datasetName]}
                      >
                        {isLoading[datasetName] ? (
                          <Box sx={{ marginLeft: '2px', marginRight: '5px' }}>
                            <CircularProgress size={17} aria-hidden />
                          </Box>
                        ) : (
                          icon
                        )}
                        <span style={{ marginLeft: 8 }}>
                          Load {label} Data
                        </span>
                      </MenuItem>
                    </span>
                  </Tooltip>
                ))}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};

export default DemoDataMenu;
